import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { LogoMarkStatic } from "@/components/LogoMark";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated");
      setTimeout(() => navigate("/login", { replace: true }), 2000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-[#05070A] text-slate-100 flex items-center justify-center px-6">
        <div className="ae-card p-8 max-w-md text-center">
          <h2 className="font-heading text-2xl text-rose-400">Invalid reset link</h2>
          <p className="text-sm text-slate-400 mt-2">This link is missing a token.</p>
          <Link to="/forgot" className="btn-primary text-sm mt-4 inline-block">Request a new link</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#05070A] text-slate-100 flex items-center justify-center px-6 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" />

      <div className="relative z-10 w-full max-w-md">
        <div className="ae-card p-8">
          <div className="flex items-center justify-center mb-6">
            <LogoMarkStatic className="h-20 md:h-[144px]" />
          </div>

          {done ? (
            <div className="text-center py-4" data-testid="reset-done">
              <CheckCircle2 className="h-12 w-12 text-emerald-400 mx-auto mb-4" />
              <h2 className="font-heading text-2xl tracking-tight">Password updated.</h2>
              <p className="text-sm text-slate-400 mt-2">Redirecting to sign in…</p>
            </div>
          ) : (
            <>
              <div className="text-xs uppercase tracking-widest text-emerald-400 mb-2">Choose a new password</div>
              <h1 className="font-heading text-3xl tracking-tight">Set new password.</h1>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">New password</label>
                  <input
                    data-testid="reset-password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
                <div>
                  <label className="block text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Confirm new password</label>
                  <input
                    data-testid="reset-confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    minLength={6}
                    className="w-full bg-[#0A0D12] border border-white/10 rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:border-emerald-500/60"
                  />
                </div>
                <button type="submit" disabled={busy} data-testid="reset-submit" className="btn-primary w-full">
                  {busy ? "Updating…" : "Update password"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
